import { useEffect, useState } from "react";
import api from "@/lib/api";
import { IndianRupee, TrendingUp, TrendingDown, Package, PieChart, Download } from "lucide-react";
import { API } from "@/lib/api";

function KPI({ label, value, icon: Icon, accent, tid }) {
  return (
    <div className="kpi-card" data-testid={tid}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon className={`w-4 h-4 ${accent || "text-zinc-400"}`} />
      </div>
      <div className="mt-3 font-display text-3xl text-zinc-900">{value}</div>
    </div>
  );
}

export default function Reports() {
  const [inward, setInward] = useState([]);
  const [outward, setOutward] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/inward"), api.get("/outward")]).then(([i, o]) => {
      setInward(i.data);
      setOutward(o.data);
    });
  }, []);

  const totalPurchase = inward.reduce((s, x) => s + (x.total_cost || 0), 0);
  const totalSales = outward.reduce((s, x) => s + (x.total_price || 0), 0);
  const unitsSold = outward.reduce((s, x) => s + (x.quantity || 0), 0);
  const grossMargin = totalSales - totalPurchase;

  // Sales by battery type (from inward join)
  const inwardById = inward.reduce((acc, x) => {
    acc[x.batch_number] = x;
    return acc;
  }, {});
  const salesByType = outward.reduce((acc, x) => {
    const batchInfo = inwardById[x.batch_number];
    const type = batchInfo?.battery_type || "Unknown";
    acc[type] = (acc[type] || 0) + (x.total_price || 0);
    return acc;
  }, {});
  const salesByTypeMax = Math.max(1, ...Object.values(salesByType));

  // Purchase vs sales by month (last 6 months)
  const byMonth = {};
  inward.forEach((x) => {
    const m = (x.purchase_date || "").slice(0, 7);
    if (!m) return;
    if (!byMonth[m]) byMonth[m] = { month: m, purchase: 0, sales: 0 };
    byMonth[m].purchase += x.total_cost || 0;
  });
  outward.forEach((x) => {
    const m = (x.dispatch_date || "").slice(0, 7);
    if (!m) return;
    if (!byMonth[m]) byMonth[m] = { month: m, purchase: 0, sales: 0 };
    byMonth[m].sales += x.total_price || 0;
  });
  const months = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month)).slice(-6);

  return (
    <div className="p-8 space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-2"><span className="inline-block w-6 h-[2px] bg-[#28B516] mr-2 align-middle" />Analytics</div>
          <h1 className="font-display text-4xl uppercase text-zinc-900">Reports</h1>
          <div className="text-xs text-zinc-500 mt-2">Business and inventory summaries</div>
        </div>
        <div className="flex gap-2">
          <a href={`${API}/export/inward`} className="btn-ghost flex items-center gap-2" data-testid="report-export-inward"><Download className="w-4 h-4" /> Inward CSV</a>
          <a href={`${API}/export/outward`} className="btn-ghost flex items-center gap-2" data-testid="report-export-outward"><Download className="w-4 h-4" /> Outward CSV</a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Total Purchase" value={`₹${totalPurchase.toLocaleString()}`} icon={IndianRupee} accent="text-zinc-500" tid="r-purchase" />
        <KPI label="Total Sales" value={`₹${totalSales.toLocaleString()}`} icon={TrendingUp} accent="text-[#28B516]" tid="r-sales" />
        <KPI label="Gross Margin" value={`₹${grossMargin.toLocaleString()}`} icon={grossMargin >= 0 ? TrendingUp : TrendingDown} accent={grossMargin >= 0 ? "text-[#28B516]" : "text-[#D9363E]"} tid="r-margin" />
        <KPI label="Units Sold" value={unitsSold} icon={Package} accent="text-[#1769AA]" tid="r-units" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-zinc-200 bg-white rounded-2xl p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-4 h-4 text-[#28B516]" />
            <div className="font-display text-lg uppercase text-zinc-900">Sales by Battery Type</div>
          </div>
          {Object.keys(salesByType).length === 0 ? (
            <div className="text-center py-8 text-zinc-500 text-sm">No sales recorded yet</div>
          ) : (
            <div className="space-y-4" data-testid="sales-by-type">
              {Object.entries(salesByType).sort((a, b) => b[1] - a[1]).map(([type, value]) => (
                <div key={type}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-zinc-900">{type}</span>
                    <span className="font-mono text-zinc-700">₹{value.toLocaleString()}</span>
                  </div>
                  <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(value / salesByTypeMax) * 100}%`, background: "linear-gradient(90deg, #20A932, #91E52A)" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-zinc-200 bg-white rounded-2xl p-6">
          <div className="overline mb-3">Snapshot</div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Inward records</span>
              <span className="font-mono font-semibold">{inward.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Outward records</span>
              <span className="font-mono font-semibold">{outward.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-600">Avg. sale ₹/unit</span>
              <span className="font-mono font-semibold">₹{unitsSold ? Math.round(totalSales / unitsSold).toLocaleString() : 0}</span>
            </div>
            <div className="flex items-center justify-between border-t border-zinc-100 pt-3 mt-3">
              <span className="text-zinc-600">Margin %</span>
              <span className={`font-mono font-semibold ${grossMargin >= 0 ? "text-[#28B516]" : "text-[#D9363E]"}`}>
                {totalSales ? `${((grossMargin / totalSales) * 100).toFixed(1)}%` : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white rounded-2xl p-6">
        <div className="font-display text-lg uppercase text-zinc-900 mb-4">Monthly Movement</div>
        {months.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm">No data</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="monthly-table">
              <thead><tr><th>Month</th><th className="text-right">Purchases (₹)</th><th className="text-right">Sales (₹)</th><th className="text-right">Margin (₹)</th></tr></thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.month}>
                    <td className="font-mono">{m.month}</td>
                    <td className="text-right">{m.purchase.toLocaleString()}</td>
                    <td className="text-right">{m.sales.toLocaleString()}</td>
                    <td className={`text-right font-semibold ${m.sales - m.purchase >= 0 ? "text-[#28B516]" : "text-[#D9363E]"}`}>
                      {(m.sales - m.purchase).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
